import DOMPurify from 'dompurify';

// Content Security Policy configuration
export const CSP_CONFIG = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'unsafe-inline'", 'https://www.youtube.com', 'https://player.vimeo.com'],
  'style-src': ["'self'", "'unsafe-inline'"],
  'img-src': ["'self'", 'data:', 'blob:', 'https:'],
  'media-src': ["'self'", 'data:', 'blob:', 'https:'],
  'frame-src': ['https://www.youtube.com', 'https://player.vimeo.com'],
  'connect-src': ["'self'", 'https:'],
  'font-src': ["'self'", 'data:', 'https:'],
  'object-src': ["'none'"],
  'base-uri': ["'self'"],
  'form-action': ["'self'"]
};

// File validation constants
export const FILE_VALIDATION = {
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/quicktime'],
  ALLOWED_DOCUMENT_TYPES: ['application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
};

// HTML sanitization with strict rules
export const sanitizeHTML = (content: string): string => {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
      'strong', 'em', 'u', 'br', 'img', 'video', 
      'iframe', 'div', 'span', 'ul', 'ol', 'li',
      'blockquote', 'a'
    ],
    ALLOWED_ATTR: [
      'src', 'alt', 'style', 'class', 'width', 'height', 
      'controls', 'frameborder', 'allowfullscreen',
      'href', 'target', 'rel'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data|youtube|vimeo):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    // Remove dangerous protocols
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover'],
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'textarea', 'button']
  });
};

// Input validation and sanitization
export const validateAndSanitizeInput = (input: string, maxLength: number = 1000): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  // Remove null bytes and control characters
  const cleaned = input.replace(/[\x00-\x1F\x7F]/g, '');
  
  // Limit length
  const truncated = cleaned.slice(0, maxLength);
  
  // Basic XSS prevention for form inputs
  return truncated
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// File validation
export const validateFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file size
  if (file.size > FILE_VALIDATION.MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `Datei ist zu groß. Maximum: ${FILE_VALIDATION.MAX_FILE_SIZE / (1024 * 1024)}MB`
    };
  }

  // Check file type
  const allAllowedTypes = [
    ...FILE_VALIDATION.ALLOWED_IMAGE_TYPES,
    ...FILE_VALIDATION.ALLOWED_VIDEO_TYPES,
    ...FILE_VALIDATION.ALLOWED_DOCUMENT_TYPES
  ];

  if (!allAllowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Dateityp nicht erlaubt'
    };
  }

  // Check file name for suspicious patterns
  const suspiciousPatterns = [
    /\.(exe|bat|cmd|scr|pif|com|vbs|js|jar|php|asp|jsp)$/i,
    /^\./,  // Hidden files
    /[<>:"|?*]/  // Invalid filename characters
  ];

  if (suspiciousPatterns.some(pattern => pattern.test(file.name))) {
    return {
      isValid: false,
      error: 'Dateiname enthält nicht erlaubte Zeichen'
    };
  }

  return { isValid: true };
};

// URL validation for external content
export const validateURL = (url: string): { isValid: boolean; error?: string } => {
  try {
    const urlObj = new URL(url);
    
    // Only allow HTTPS (or HTTP for localhost/development)
    if (!['https:', 'http:'].includes(urlObj.protocol)) {
      return {
        isValid: false,
        error: 'Nur HTTPS URLs sind erlaubt'
      };
    }

    // Block suspicious domains
    const blockedDomains = [
      'localhost',
      '127.0.0.1',
      '0.0.0.0',
      '::1'
    ];

    if (urlObj.protocol === 'http:' && !blockedDomains.includes(urlObj.hostname)) {
      return {
        isValid: false,
        error: 'HTTP URLs sind nur für localhost erlaubt'
      };
    }

    return { isValid: true };
  } catch {
    return {
      isValid: false,
      error: 'Ungültige URL'
    };
  }
};

// Rate limiting helper
export const createRateLimit = (maxRequests: number, windowMs: number) => {
  const requests = new Map<string, number[]>();

  return (identifier: string): boolean => {
    const now = Date.now();
    const windowStart = now - windowMs;
    
    if (!requests.has(identifier)) {
      requests.set(identifier, []);
    }
    
    const userRequests = requests.get(identifier)!;
    
    // Remove old requests outside the window
    const validRequests = userRequests.filter(time => time > windowStart);
    
    if (validRequests.length >= maxRequests) {
      return false; // Rate limit exceeded
    }
    
    validRequests.push(now);
    requests.set(identifier, validRequests);
    
    return true; // Request allowed
  };
};

// Generate CSP header string
export const generateCSPHeader = (): string => {
  return Object.entries(CSP_CONFIG)
    .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
    .join('; ');
};